// Sonio Dashboard - Converted to Tailwind CSS
import React from 'react';
import Chart from 'react-apexcharts';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { ApexOptions } from 'apexcharts';
import axios from 'axios';
import { format } from 'date-fns';
import { SonioDemoFlowFilterContext } from 'src/contexts/SonioDemoFlowFilterContext';
import { useAppSelector } from 'src/hooks/useStore';
import {
  buildFlowNameLookup,
  isSonioDemoOrganization,
} from 'src/services/organizations.service';
import { getCmsApiBaseUrl } from 'src/services/api.config';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import {
  Activity,
  ArrowUpRight,
  Calendar,
  ChevronRight,
  FileText,
  Headset,
  Info,
  LayoutList,
  X,
} from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@radix-ui/react-popover';

const TIPICO_AFFORDABILITY_SPORTS_GERMANY_ALLOWED_FLOW_IDS = [
  '65e877d12156d096dbe51ca0',
  '686bca6cf9073ffc24b2ff8c',
  '65e877632156d096dbe51c9e',
  '65e877c02156d096dbe51c9f',
];

const TIPICO_IDV_SPORTS_GERMANY_ALLOWED_FLOW_IDS = [
  '67d1537a0816c4f8be3981f2',
  '69aea238c9aa438c178a58f6',
];

// Flow IDs that should not contribute to Session Trends and should not appear in flow filters.
const TIPICO_SPORTS_TEST_UPLOAD_HIDDEN_FLOW_IDS = [
  '667bd06920b3b7d3d3a64660',
  '65ca169f8db0af6615cc61e7',
  '667bd07020b3b7d3d3a64661',
  '667bd07920b3b7d3d3a64662',
  '66a0ddf810e4b3d220f82de0',
  '67112cbb5507b606aa9cdbd5',
  '673c660560d53e97ce601d9d',
];

const TIPICO_SPORTS_TEST_UPLOAD_HIDDEN_FLOW_ID_SET = new Set(
  TIPICO_SPORTS_TEST_UPLOAD_HIDDEN_FLOW_IDS,
);

// const getTopSectionColor = (status: string, growth: string) => {
//   const isPositive = growth.startsWith('+');
//   const isNegative = growth.startsWith('-');

//   const typeRules: Record<string, boolean> = {
//     'Needs Review': false,
//     Unassigned: false,
//     'Assigned to Me': true,
//   };

//   const positiveIsGood = typeRules[status] ?? true;
//   const isGood =
//     (isPositive && positiveIsGood) || (isNegative && !positiveIsGood);

//   return isGood ? 'bg-[#025F0D1A]' : 'bg-[#B215151A]';
// };

// const statCards = [
//   {
//     title: 'Needs Review',
//     value: '40',
//     growth: '+12.5%',
//     type: 'review',
//     color: 'text-black',
//     bgColor: 'bg-transparent',
//   },
//   {
//     title: 'Unassigned',
//     value: '21',
//     growth: '-8.2%',
//     color: 'text-black',
//     bgColor: 'bg-transparent',
//     type: 'pending',
//   },
//   {
//     title: 'Assigned to Me',
//     value: '8',
//     growth: '+3.1%',
//     color: 'text-black',
//     bgColor: 'bg-transparent',
//     type: 'success',
//   },
// ];
const getStatCardsConfig = () => [
  { title: 'Pass', key: 'pass', dot: '#6ECF89' },
  { title: 'Fail', key: 'fail', dot: '#B21515' },
  { title: 'Needs Review', key: 'needsreview', dot: '#E99400' },
  { title: 'Pending', key: 'pending', dot: '#5B6B80' },
];

// const getCardStyles = (type: string, growth: string) => {
//   const isPositive = growth.startsWith('+');
//   const isNegative = growth.startsWith('-');

//   const typeRules: Record<string, boolean> = {
//     success: true,
//     failed: false,
//     review: false,
//     pending: false,
//   };

//   const positiveIsGood = typeRules[type] ?? true;
//   const isGood =
//     (isPositive && positiveIsGood) || (isNegative && !positiveIsGood);

//   return {
//     growthColor: 'text-white',
//     growthBgColor: isGood ? 'bg-[#025F0D]' : 'bg-[#B21515]',
//   };
// };

const quickLinks = [
  {
    label: 'Session overview',
    description: 'Browse and review sessions',
    icon: LayoutList,
    link: '/transactions/sessions',
  },
  {
    label: 'API documentation',
    description: 'Endpoints, webhooks and SDKs',
    icon: FileText,
    link: '/api-docs',
  },
  {
    label: 'Service management',
    description: 'Open a support ticket',
    icon: Headset,
    link: 'https://sonioid.atlassian.net/servicedesk/customer/portals',
  },
];

const chartOptions: ApexOptions = {
  chart: {
    type: 'line' as const,
    sparkline: { enabled: false },
    toolbar: { show: false },
    zoom: { enabled: false },
    background: 'transparent',
    fontFamily: 'inherit',
  },
  stroke: {
    curve: 'smooth' as const,
    width: 2.5,
  },
  grid: {
    show: true,
    borderColor: '#E9EDF3',
    strokeDashArray: 4,
    xaxis: { lines: { show: false } },
    yaxis: { lines: { show: true } },
  },
  xaxis: {
    categories: [] as string[],
    labels: {
      show: true,
      style: {
        colors: '#5B6B80',
        fontSize: '12px',
        fontWeight: 500,
      },
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
    crosshairs: { stroke: { color: '#00387D', dashArray: 3 } },
  },
  yaxis: {
    labels: {
      show: true,
      style: {
        colors: '#5B6B80',
        fontSize: '12px',
      },
    },
    axisBorder: { show: false },
    axisTicks: { show: false },
    min: 0,
    max: 700,
    tickAmount: 4,
  },
  tooltip: {
    enabled: true,
    shared: true,
    intersect: false,
    theme: 'light',
  },
  colors: ['#00387D', '#6ECF89', '#B21515', '#E99400'],
  dataLabels: { enabled: false },
  markers: {
    size: 0,
    hover: { size: 5 },
  },
  legend: { show: false },
};

/** CMS validation colors (same as Sessions recap / badges). */
const VALIDATION_TREND_META = [
  {
    key: 'pass' as const,
    label: 'Pass',
    apiKeys: ['pass', 'Pass', 'PASS'],
    color: '#6ECF89',
  },
  {
    key: 'fail' as const,
    label: 'Fail',
    apiKeys: ['fail', 'Fail', 'FAIL'],
    color: '#B21515',
  },
  {
    key: 'needsReview' as const,
    label: 'Needs review',
    apiKeys: [
      'needsReview',
      'needsreview',
      'NeedsReview',
      'NEEDS_REVIEW',
      'needs_review',
    ],
    color: '#E99400',
  },
] as const;

type ValidationTrendKey = (typeof VALIDATION_TREND_META)[number]['key'];

function readTrendCount(
  item: Record<string, unknown>,
  keys: readonly string[],
) {
  for (const key of keys) {
    const value = item[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
      const n = Number(value);
      if (Number.isFinite(n)) return n;
    }
  }
  return 0;
}

const TOTAL_COMPLETED_TREND = {
  name: 'Total completed sessions',
  color: '#00387D',
  apiKeys: [
    'total',
    'Total',
    'TOTAL',
    'completed',
    'Completed',
    'COMPLETED',
    'totalCompleted',
    'completedSessions',
  ],
} as const;

function readCompletedTotal(item: Record<string, unknown>): number {
  const fromApi = readTrendCount(item, TOTAL_COMPLETED_TREND.apiKeys);
  if (fromApi > 0) return fromApi;
  // Fallback: sum Pass + Fail + Needs review for that bucket
  return VALIDATION_TREND_META.reduce(
    (sum, meta) => sum + readTrendCount(item, meta.apiKeys),
    0,
  );
}

// Helper function to transform API data to chart format (completed-session validation trends)
const transformTrendDataToChart = (
  data: any[],
  selectedValidations: ValidationTrendKey[],
) => {
  const validationSeries = VALIDATION_TREND_META.filter(meta =>
    selectedValidations.includes(meta.key),
  ).map(meta => ({
    name: meta.label,
    type: 'line',
    data: data.map(item =>
      readTrendCount((item ?? {}) as Record<string, unknown>, meta.apiKeys),
    ),
  }));

  const totalSeries = {
    name: TOTAL_COMPLETED_TREND.name,
    type: 'area',
    data: data.map(item =>
      readCompletedTotal((item ?? {}) as Record<string, unknown>),
    ),
  };

  return [totalSeries, ...validationSeries];
};

const QUARTER_LABELS = ['JAN-MAR', 'APR-JUN', 'JUL-SEP', 'OCT-DEC'] as const;

function quarterLabelFromMonth(month: number): string {
  if (month >= 1 && month <= 3) return QUARTER_LABELS[0];
  if (month >= 4 && month <= 6) return QUARTER_LABELS[1];
  if (month >= 7 && month <= 9) return QUARTER_LABELS[2];
  return QUARTER_LABELS[3];
}

// Helper function to generate categories based on date format
const generateCategories = (data: any[], interval: string) => {
  return data.map(item => {
    const raw = String(item.date ?? '');

    if (interval === 'quarterly') {
      // "2025-Q1" / "Q1" / "Q1 2025"
      const qMatch = raw.match(/Q([1-4])/i);
      if (qMatch) {
        return QUARTER_LABELS[parseInt(qMatch[1], 10) - 1];
      }
      // "2025-01" / "2025-1" / "2025-01-01"
      const parts = raw.split('-');
      if (parts.length >= 2) {
        const month = parseInt(parts[1], 10);
        if (Number.isFinite(month) && month >= 1 && month <= 12) {
          return quarterLabelFromMonth(month);
        }
      }
      // Bare quarter index "1".."4"
      const qNum = parseInt(raw, 10);
      if (qNum >= 1 && qNum <= 4) {
        return QUARTER_LABELS[qNum - 1];
      }
      return raw || '—';
    }

    if (interval === 'monthly') {
      // Convert "2025-10" to "OCT"
      const [, month] = raw.split('-');
      const monthNames = [
        'JAN',
        'FEB',
        'MAR',
        'APR',
        'MAY',
        'JUN',
        'JUL',
        'AUG',
        'SEP',
        'OCT',
        'NOV',
        'DEC',
      ];
      return monthNames[parseInt(month, 10) - 1];
    }
    if (interval === 'weekly') {
      // For weekly, show week number or date range
      return `W${raw}`;
    }
    // For daily, show day
    return raw.split('-').slice(1).join('/'); // MM/DD
  });
};

const SonioDashboard = () => {
  const [timePeriod, setTimePeriod] = React.useState('monthly');
  const [chartKey, setChartKey] = React.useState(0);
  const { user, getIdTokenClaims } = useAuth0();
  const navigate = useNavigate();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const chartContainerRef = React.useRef<HTMLDivElement>(null);

  const formatRecapCount = (value: unknown) => {
    const num =
      typeof value === 'number'
        ? value
        : typeof value === 'string'
          ? Number(value)
          : NaN;

    if (!Number.isFinite(num)) return String(value ?? '');

    // Keep small numbers readable; compact large ones to avoid breaking the UI.
    if (Math.abs(num) >= 1_000_000) {
      const rounded = Math.round((num / 1_000_000) * 10) / 10;
      return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded}M`;
    }
    if (Math.abs(num) >= 10_000) {
      return `${Math.round(num / 1_000)}k`;
    }
    return String(num);
  };

  // Redux state
  const { selectedOrganization, organizations } = useAppSelector(
    state => state.organization,
  );
  const { liveData } = useAppSelector(state => state.sliceTransaction);
  const sonioDemoFlowFilter = React.useContext(SonioDemoFlowFilterContext);
  const isSonioDemoOrg = isSonioDemoOrganization(selectedOrganization);

  const flowNameLookup = React.useMemo(
    () => buildFlowNameLookup(organizations),
    [organizations],
  );

  const isTipicoAffordabilitySportsGermany =
    selectedOrganization?.display_name?.trim().toLowerCase() ===
      'tipico affordability sports germany' ||
    selectedOrganization?.name?.trim().toLowerCase().replace(/_/g, ' ') ===
      'tipico sports';

  const isTipicoIdvSportsGermany =
    selectedOrganization?.display_name?.trim().toLowerCase() ===
      'tipico idv sports germany' ||
    selectedOrganization?.name?.trim().toLowerCase().replace(/_/g, ' ') ===
      'tipico idv sports germany';
  // In Sonio Demo org: use sidebar checkbox selection. If the user hasn't
  // ticked anything yet, fall back to *their own* profile flows (from
  // /profile.flowids) so we never leak other users' data from the same org.
  const selectedFlowIdsFromContext = React.useMemo(() => {
    if (!isSonioDemoOrg || !sonioDemoFlowFilter) return null;
    if (sonioDemoFlowFilter.selectedFlowIds.length > 0) {
      return sonioDemoFlowFilter.selectedFlowIds;
    }
    if (sonioDemoFlowFilter.profileFlowIds.length > 0) {
      return sonioDemoFlowFilter.profileFlowIds;
    }
    return selectedOrganization?.flowIds ?? [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSonioDemoOrg,
    sonioDemoFlowFilter,
    sonioDemoFlowFilter?.selectedFlowIds,
    sonioDemoFlowFilter?.profileFlowIds,
    selectedOrganization?.flowIds,
  ]);

  // API data state
  const [trendData, setTrendData] = React.useState<any[]>([]);
  const [isLoadingTrends, setIsLoadingTrends] = React.useState(false);
  /** Empty / all selectable flows = aggregate all flows. */
  const [selectedFlowIds, setSelectedFlowIds] = React.useState<string[]>([]);
  /** Explicit "All flows" mode — not inferred from selecting every pill. */
  const [allFlowsMode, setAllFlowsMode] = React.useState(true);
  const [selectedValidations, setSelectedValidations] = React.useState<
    ValidationTrendKey[]
  >(['pass', 'fail', 'needsReview']);
  const [validationStats, setValidationStats] = React.useState({
    pass: 0,
    fail: 0,
    needsreview: 0,
    pending: 0,
  });
  const [isLoadingStats, setIsLoadingStats] = React.useState(false);

  // Date range state - default to last 6 months
  const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 5);
    return {
      start: start.toISOString().split('T')[0], // YYYY-MM-DD
      end: end.toISOString().split('T')[0],
    };
  };

  const [startDate, setStartDate] = React.useState(getDefaultDateRange().start);
  const [endDate, setEndDate] = React.useState(getDefaultDateRange().end);
  const [startDateObj, setStartDateObj] = React.useState<Date | null>(
    new Date(getDefaultDateRange().start),
  );
  const [endDateObj, setEndDateObj] = React.useState<Date | null>(
    new Date(getDefaultDateRange().end),
  );
  const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);

  // Removed generateMonthOptions - now using DateCalendar components

  // Get current chart data and categories based on selected time period (API only — no fake fallback)
  const activeValidationMeta = VALIDATION_TREND_META.filter(meta =>
    selectedValidations.includes(meta.key),
  );
  const currentChartData =
    trendData.length > 0
      ? transformTrendDataToChart(trendData, selectedValidations)
      : [
          {
            name: TOTAL_COMPLETED_TREND.name,
            type: 'area',
            data: [] as number[],
          },
          ...activeValidationMeta.map(meta => ({
            name: meta.label,
            type: 'line',
            data: [] as number[],
          })),
        ];
  const currentCategories =
    trendData.length > 0 ? generateCategories(trendData, timePeriod) : [];
  const activeTrendColors = [
    TOTAL_COMPLETED_TREND.color,
    ...activeValidationMeta.map(meta => meta.color),
  ];

  // Calculate dynamic y-axis max based on data
  const calculateYAxisMax = (data: any[]) => {
    const allValues = data.flatMap(series => series.data);
    if (allValues.length === 0) return 10;
    const maxValue = Math.max(...allValues);
    // Add 20% padding to the top for better visualization
    return Math.ceil(maxValue * 1.2) || 10;
  };

  const yAxisMax =
    currentChartData.length > 0 ? calculateYAxisMax(currentChartData) : 700;

  // Update chart options with current categories and dynamic y-axis
  const currentChartOptions = {
    ...chartOptions,
    colors: activeTrendColors,
    fill: {
      type: activeTrendColors.map((_, i) => (i === 0 ? 'gradient' : 'solid')),
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0,
        opacityFrom: 0.18,
        opacityTo: 0,
        stops: [0, 95],
      },
    },
    xaxis: {
      ...chartOptions.xaxis,
      categories: currentCategories,
    },
    yaxis: {
      ...chartOptions.yaxis,
      max: yAxisMax,
      tickAmount: 5,
    },
  };

  // Get available flow categories: for Sonio Demo use only profile flow IDs (sidebar context), else org flowIds
  const getFlowCategories = React.useMemo(() => {
    if (isSonioDemoOrg) {
      // Sonio Demo: show only the flow IDs from profile (sidebar context); show nothing until loaded
      const profileFlowIds = selectedFlowIdsFromContext ?? [];
      const visibleProfileFlowIds = profileFlowIds.filter(
        (flowId: string) =>
          !TIPICO_SPORTS_TEST_UPLOAD_HIDDEN_FLOW_ID_SET.has(flowId),
      );
      if (visibleProfileFlowIds.length === 0) return [];
      return [
        { id: 'ALL', name: 'ALL', count: visibleProfileFlowIds.length },
        ...visibleProfileFlowIds.map((flowId: string) => ({
          id: flowId,
          name: flowNameLookup[flowId] || flowId,
          count: 1,
        })),
      ];
    }

    if (
      !selectedOrganization?.flowIds ||
      selectedOrganization.flowIds.length === 0
    ) {
      return [];
    }

    // Other orgs: filter out Rootz flow ID
    let filteredFlowIds = selectedOrganization.flowIds.filter(
      (flowId: string) => flowId !== '65c3505ae446fc3e87772607',
    );

    // Tipico Affordability Sports Germany: show only a curated subset
    if (isTipicoAffordabilitySportsGermany) {
      filteredFlowIds = filteredFlowIds.filter(flowId =>
        TIPICO_AFFORDABILITY_SPORTS_GERMANY_ALLOWED_FLOW_IDS.includes(flowId),
      );
    }

    // Tipico IDV Sports Germany: show only a curated subset
    if (isTipicoIdvSportsGermany) {
      filteredFlowIds = filteredFlowIds.filter(flowId =>
        TIPICO_IDV_SPORTS_GERMANY_ALLOWED_FLOW_IDS.includes(flowId),
      );
    }

    // For any non-curated org: hide the "Tipico Sports Test upload" flows
    if (!isTipicoAffordabilitySportsGermany && !isTipicoIdvSportsGermany) {
      filteredFlowIds = filteredFlowIds.filter(
        (flowId: string) =>
          !TIPICO_SPORTS_TEST_UPLOAD_HIDDEN_FLOW_ID_SET.has(flowId),
      );
    }

    return [
      { id: 'ALL', name: 'ALL', count: filteredFlowIds.length },
      ...filteredFlowIds.map((flowId: string) => ({
        id: flowId,
        name: flowNameLookup[flowId] || flowId,
        count: 1,
      })),
    ];
  }, [
    selectedOrganization,
    isSonioDemoOrg,
    selectedFlowIdsFromContext,
    isTipicoAffordabilitySportsGermany,
    isTipicoIdvSportsGermany,
    flowNameLookup,
  ]);

  const selectableFlows = React.useMemo(
    () => getFlowCategories.filter(flow => flow.id !== 'ALL'),
    [getFlowCategories],
  );
  const allFlowsSelected = allFlowsMode;
  const activeFlowIds = React.useMemo(
    () =>
      allFlowsMode ? selectableFlows.map(flow => flow.id) : selectedFlowIds,
    [allFlowsMode, selectableFlows, selectedFlowIds],
  );
  const selectedValidationsKey = selectedValidations.join('|');

  const selectableFlowIdsKey = React.useMemo(
    () => selectableFlows.map(f => f.id).join('|'),
    [selectableFlows],
  );

  // Keep selection in sync when org/flow list changes
  React.useEffect(() => {
    setSelectedFlowIds(
      selectableFlowIdsKey ? selectableFlowIdsKey.split('|') : [],
    );
    setAllFlowsMode(true);
  }, [selectedOrganization?.id, selectableFlowIdsKey]);

  const toggleFlowSelection = (flowId: string) => {
    setAllFlowsMode(false);
    setSelectedFlowIds(prev => {
      // Coming from All flows: select only the clicked flow
      if (allFlowsMode) {
        return [flowId];
      }

      if (prev.includes(flowId)) {
        const next = prev.filter(id => id !== flowId);
        // Keep at least one flow selected
        return next.length === 0 ? prev : next;
      }

      return [...prev, flowId];
    });
  };

  const selectAllFlows = () => {
    setAllFlowsMode(true);
    setSelectedFlowIds(selectableFlows.map(flow => flow.id));
  };

  const toggleValidationSelection = (key: ValidationTrendKey) => {
    setSelectedValidations(prev => {
      if (prev.includes(key)) {
        return prev.length === 1 ? prev : prev.filter(item => item !== key);
      }
      return [...prev, key];
    });
  };

  // Fetch trends data from API
  const fetchTrends = React.useCallback(async () => {
    if (!selectedOrganization) {
      return;
    }

    setIsLoadingTrends(true);
    try {
      const { __raw } = await getIdTokenClaims();
      const baseUrl = `${getCmsApiBaseUrl(liveData)}/sessions/trends`;

      const params = new URLSearchParams({
        startDate,
        endDate,
        interval: timePeriod,
        // Completed sessions only — backend should filter to completed validations
        completedOnly: 'true',
      });

      // Sonio Demo org: use only the picked flow IDs from sidebar (no customerId)
      if (
        isSonioDemoOrg &&
        selectedFlowIdsFromContext &&
        selectedFlowIdsFromContext.length > 0
      ) {
        const demoFlows = selectedFlowIdsFromContext.filter(
          (id: string) => !TIPICO_SPORTS_TEST_UPLOAD_HIDDEN_FLOW_ID_SET.has(id),
        );
        const flowsToSend =
          allFlowsSelected || activeFlowIds.length === 0
            ? demoFlows
            : activeFlowIds.filter(id => demoFlows.includes(id));
        flowsToSend.forEach(id => params.append('flowIds', id));
      } else {
        const customerId =
          selectedOrganization.id === ''
            ? ''
            : selectedOrganization.customerId || '';
        params.append('customerId', customerId);

        let flowsToSend = activeFlowIds.filter(
          id => !TIPICO_SPORTS_TEST_UPLOAD_HIDDEN_FLOW_ID_SET.has(id),
        );

        if (isTipicoAffordabilitySportsGermany) {
          flowsToSend = (
            allFlowsSelected || flowsToSend.length === 0
              ? TIPICO_AFFORDABILITY_SPORTS_GERMANY_ALLOWED_FLOW_IDS
              : flowsToSend
          ).filter(id =>
            TIPICO_AFFORDABILITY_SPORTS_GERMANY_ALLOWED_FLOW_IDS.includes(id),
          );
        } else if (isTipicoIdvSportsGermany) {
          flowsToSend = (
            allFlowsSelected || flowsToSend.length === 0
              ? TIPICO_IDV_SPORTS_GERMANY_ALLOWED_FLOW_IDS
              : flowsToSend
          ).filter(id =>
            TIPICO_IDV_SPORTS_GERMANY_ALLOWED_FLOW_IDS.includes(id),
          );
        } else if (allFlowsSelected || flowsToSend.length === 0) {
          flowsToSend = (selectedOrganization.flowIds ?? []).filter(
            (flowId: string) =>
              flowId !== '65c3505ae446fc3e87772607' &&
              !TIPICO_SPORTS_TEST_UPLOAD_HIDDEN_FLOW_ID_SET.has(flowId),
          );
        }

        flowsToSend.forEach(id => params.append('flowIds', id));
      }

      const response = await axios.get(`${baseUrl}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${__raw}`,
        },
      });

      setTrendData(response.data.data || []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching trends:', error);
      setTrendData([]);
    } finally {
      setIsLoadingTrends(false);
    }
  }, [
    selectedOrganization,
    startDate,
    endDate,
    timePeriod,
    activeFlowIds,
    allFlowsSelected,
    selectedFlowIdsFromContext,
    isSonioDemoOrg,
    isTipicoAffordabilitySportsGermany,
    isTipicoIdvSportsGermany,
    liveData,
    getIdTokenClaims,
  ]);

  // Fetch trends on mount and when dependencies change
  React.useEffect(() => {
    fetchTrends();
  }, [fetchTrends]);

  // Bump chart remount when visible series change
  React.useEffect(() => {
    setChartKey(k => k + 1);
  }, [selectedValidationsKey, timePeriod, trendData]);

  // Fetch validation stats from API
  const fetchValidationStats = React.useCallback(async () => {
    setIsLoadingStats(true);
    try {
      const { __raw } = await getIdTokenClaims();
      const baseUrl = `${getCmsApiBaseUrl(liveData)}/sessions/validation-stats`;

      const params = new URLSearchParams();
      if (selectedFlowIdsFromContext && selectedFlowIdsFromContext.length > 0) {
        // Sonio Demo: use flowIds, omit authOrgId
        params.append('flowIds', selectedFlowIdsFromContext.join(','));
      } else {
        params.append('authOrgId', selectedOrganization?.id || '');
      }

      const response = await axios.get(`${baseUrl}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${__raw}`,
        },
      });

      setValidationStats({
        pass: response.data.pass || 0,
        fail: response.data.fail || 0,
        needsreview: response.data.needsreview || 0,
        pending: response.data.pending || 0,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching validation stats:', error);
      setValidationStats({
        pass: 0,
        fail: 0,
        needsreview: 0,
        pending: 0,
      });
    } finally {
      setIsLoadingStats(false);
    }
  }, [
    selectedOrganization,
    liveData,
    getIdTokenClaims,
    selectedFlowIdsFromContext,
  ]);

  // Fetch validation stats on mount and when organization changes
  React.useEffect(() => {
    fetchValidationStats();
  }, [fetchValidationStats]);

  // Get stat cards with dynamic values
  const statCards = React.useMemo(() => {
    const config = getStatCardsConfig();
    return config.map(card => ({
      ...card,
      value:
        validationStats[card.key as keyof typeof validationStats].toString(),
    }));
  }, [validationStats]);

  // Map card titles to validation filter values
  const getValidationFilterValue = (title: string): string => {
    const mapping: Record<string, string> = {
      Pass: 'PASS',
      Fail: 'FAIL',
      'Needs Review': 'NEEDS_REVIEW',
      Pending: 'PENDING',
    };
    return mapping[title] || '';
  };

  // Handle card click to navigate to sessions with filter
  const handleCardClick = (cardTitle: string) => {
    const validationValue = getValidationFilterValue(cardTitle);
    if (validationValue) {
      const queryParams = new URLSearchParams();
      queryParams.append('validation', validationValue);
      navigate(`/transactions/sessions?${queryParams.toString()}`);
    }
  };

  // Trigger chart resize after mount to fix initial render
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setChartKey(prev => prev + 1);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Handle window resize and sidebar toggle to fix layout issues
  React.useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;
    let isMounted = true;
    let lastResizeTime = 0;
    const RESIZE_THROTTLE = 500; // Minimum time between resizes in ms

    const triggerChartResize = () => {
      const now = Date.now();
      // Throttle resizes to prevent too many rapid updates
      if (now - lastResizeTime < RESIZE_THROTTLE) {
        return;
      }

      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (isMounted && chartContainerRef.current) {
          // Check if the chart container element exists and is valid
          const container = chartContainerRef.current;
          if (
            container &&
            container instanceof Element &&
            container.isConnected &&
            container.offsetWidth > 0 &&
            container.offsetHeight > 0
          ) {
            // Only resize if container has dimensions
            try {
              lastResizeTime = Date.now();
              setChartKey(prev => prev + 1);
            } catch (error) {
              // eslint-disable-next-line no-console
              console.warn('Chart resize error:', error);
            }
          }
        }
      }, 300);
    };

    const handleResize = () => {
      // Trigger chart resize when window resizes (including sidebar toggle)
      triggerChartResize();
    };

    // Listen to window resize events
    window.addEventListener('resize', handleResize, { passive: true });

    // Use ResizeObserver to watch for chart container size changes only
    let resizeObserver: ResizeObserver | null = null;

    const observeChartContainer = () => {
      if (
        chartContainerRef.current &&
        chartContainerRef.current instanceof Element
      ) {
        try {
          resizeObserver = new ResizeObserver(entries => {
            // Check if entries are valid and target is an Element
            if (entries && entries.length > 0) {
              const entry = entries[0];
              if (
                entry &&
                entry.target &&
                entry.target instanceof Element &&
                entry.target.isConnected
              ) {
                triggerChartResize();
              }
            }
          });
          resizeObserver.observe(chartContainerRef.current);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn('ResizeObserver error (chart):', error);
        }
      }
    };

    // Try after a delay to ensure ref is set
    const timeoutId = setTimeout(observeChartContainer, 300);

    return () => {
      isMounted = false;
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
      clearTimeout(resizeTimeout);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  const totalSessions =
    validationStats.pass +
    validationStats.fail +
    validationStats.needsreview +
    validationStats.pending;
  const passRate =
    totalSessions > 0
      ? Math.round((validationStats.pass / totalSessions) * 1000) / 10
      : 0;

  const intervalOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
  ];

  const chipClass = (active: boolean) =>
    `h-8 px-3 rounded-full text-[13px] font-semibold border-[1.5px] transition-colors whitespace-nowrap ${
      active
        ? 'bg-[#00387D] border-[#00387D] text-white'
        : 'bg-white border-[#E5E7EB] text-[#252525] hover:border-[#00387D] hover:text-[#00387D]'
    }`;

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-[#F7F9FC] text-[#252525] px-4 sm:px-8 py-7"
      data-main-content
    >
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-[28px] font-extrabold tracking-[-0.025em] text-[#0C2340]">
          Welcome back
          {user?.given_name ? (
            <>
              , <span className="text-[#00387D]">{user.given_name}</span>
            </>
          ) : null}
        </h1>
        <p className="text-sm text-[#6B6B6B] mt-1">
          Here&apos;s what&apos;s happening across your sessions
        </p>
      </div>

      {/* Sessions overview */}
      <section className="bg-white border-[1.5px] border-[#E5E7EB] rounded-[14px] overflow-hidden mb-5 grid grid-cols-1 xl:grid-cols-[300px_minmax(0,1fr)]">
        <div className="p-6 bg-gradient-to-br from-[#EEF2F8] to-[#F8FAFC] border-b xl:border-b-0 xl:border-r border-[#E5E7EB] flex xl:flex-col justify-between gap-5 flex-wrap">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#5B6B80]">
              Total sessions
            </p>
            <p className="text-[44px] leading-none font-extrabold tracking-[-0.02em] text-[#0C2340] mt-2 tabular-nums">
              {isLoadingStats ? '–' : formatRecapCount(totalSessions)}
            </p>
          </div>
          <div className="flex items-center gap-3.5">
            <svg viewBox="0 0 36 36" className="w-[54px] h-[54px]">
              <circle
                cx="18"
                cy="18"
                r="15"
                fill="none"
                stroke="#DDE4EE"
                strokeWidth="4"
              />
              <circle
                cx="18"
                cy="18"
                r="15"
                fill="none"
                stroke="#6ECF89"
                strokeWidth="4"
                strokeLinecap="round"
                pathLength={100}
                strokeDasharray={`${passRate} 100`}
                transform="rotate(-90 18 18)"
                style={{ transition: 'stroke-dasharray 1s ease' }}
              />
            </svg>
            <div>
              <p className="text-xl font-bold text-[#0C2340] leading-tight tabular-nums">
                {isLoadingStats ? '–' : `${passRate}%`}
              </p>
              <p className="text-[13px] text-[#5B6B80]">pass rate</p>
            </div>
          </div>
        </div>

        <div className="p-5 flex flex-col gap-4 min-w-0">
          <div>
            <h3 className="text-base font-extrabold text-[#0C2340]">
              Sessions recap
            </h3>
            <p className="text-[13px] text-[#5B6B80] mt-0.5">
              Click a status to open the filtered session list
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5">
            {statCards.map(card => (
              <button
                type="button"
                key={card.key}
                onClick={() => handleCardClick(card.title)}
                className="group relative overflow-hidden text-left bg-white border-[1.5px] border-[#E5E7EB] rounded-[14px] px-3.5 pt-3.5 pb-3 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#00387D] hover:shadow-[0_8px_24px_rgba(0,56,125,0.12)]"
              >
                <span
                  className="absolute inset-x-0 top-0 h-[3px] bg-[#EEF2F7]"
                  aria-hidden="true"
                />
                <span
                  className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#00387D] to-[#6ECF89] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-[420ms]"
                  aria-hidden="true"
                />
                <span className="flex items-center justify-between mb-2.5">
                  <span
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#252525] px-2.5 py-[3px] rounded-full whitespace-nowrap"
                    style={{ backgroundColor: `${card.dot}26` }}
                  >
                    <span
                      className="w-[7px] h-[7px] rounded-full"
                      style={{ backgroundColor: card.dot }}
                    />
                    {card.title}
                  </span>
                  <ChevronRight className="w-4 h-4 text-[#5B6B80] group-hover:text-[#00387D] group-hover:translate-x-0.5 transition-all" />
                </span>
                <span className="block text-[30px] leading-none font-extrabold tracking-[-0.02em] text-[#0C2340] tabular-nums">
                  {isLoadingStats ? (
                    <span className="inline-block h-7 w-16 rounded bg-[#EEF2F7] animate-pulse align-middle" />
                  ) : (
                    formatRecapCount(card.value)
                  )}
                </span>
              </button>
            ))}
          </div>
          {totalSessions > 0 && (
            <div className="flex h-2 rounded-full overflow-hidden gap-0.5 bg-[#F7F9FC]">
              {statCards.map(card => (
                <span
                  key={card.key}
                  title={card.title}
                  style={{
                    width: `${(Number(card.value) / totalSessions) * 100}%`,
                    backgroundColor: card.dot,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-5">
        {/* Session validation trends */}
        <section className="bg-white border-[1.5px] border-[#E5E7EB] rounded-[14px] px-5 sm:px-6 pt-5 pb-3 min-w-0 flex flex-col">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-extrabold text-[#0C2340]">
                  Session validation trends
                </h2>
                <span
                  title="Only completed sessions are included."
                  className="text-[#5B6B80]"
                >
                  <Info className="w-4 h-4" />
                </span>
              </div>
              <p className="text-[13px] text-[#5B6B80] mt-0.5">
                Completed sessions only
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-2 h-[34px] px-3 border-[1.5px] border-[#E5E7EB] rounded-md text-[13px] font-semibold text-[#252525] bg-white hover:border-[#00387D] hover:text-[#00387D] transition-colors"
                      onClick={() => setIsCalendarOpen(true)}
                    >
                      <Calendar className="w-4 h-4" />
                      <span className="whitespace-nowrap">
                        {startDateObj && endDateObj
                          ? `${format(startDateObj, 'MMM dd, yyyy')} – ${format(endDateObj, 'MMM dd, yyyy')}`
                          : 'Select date range'}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="bg-white rounded-[14px] shadow-[0_12px_30px_rgba(0,56,125,0.14)] border-[1.5px] border-[#E5E7EB] p-4 z-50"
                    align="end"
                    sideOffset={6}
                  >
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center justify-between border-b border-[#E4EBF4] pb-2">
                        <h3 className="text-sm font-bold text-[#0C2340]">
                          Select date range
                        </h3>
                        <button
                          type="button"
                          onClick={() => setIsCalendarOpen(false)}
                          className="p-1 rounded hover:bg-[#F7F9FC] text-[#5B6B80]"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="flex gap-4">
                        <div className="flex flex-col gap-2">
                          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#5B6B80] text-center">
                            Start date
                          </div>
                          <DateCalendar
                            value={startDateObj}
                            onChange={(newValue: Date | null) => {
                              setStartDateObj(newValue);
                              if (newValue) {
                                setStartDate(
                                  newValue.toISOString().split('T')[0],
                                );
                                if (!endDateObj || newValue > endDateObj) {
                                  setEndDateObj(newValue);
                                  setEndDate(
                                    newValue.toISOString().split('T')[0],
                                  );
                                }
                              }
                            }}
                            maxDate={endDateObj || new Date()}
                            sx={{
                              '& .MuiPickersCalendarHeader-root': {
                                fontSize: '0.875rem',
                              },
                              '& .MuiDayCalendar-weekDayLabel': {
                                fontSize: '0.75rem',
                              },
                              '& .MuiPickersDay-root': { fontSize: '0.75rem' },
                              '& .MuiPickersDay-root.Mui-selected': {
                                backgroundColor: '#00387D',
                              },
                            }}
                          />
                        </div>

                        <div className="flex flex-col gap-2">
                          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#5B6B80] text-center">
                            End date
                          </div>
                          <DateCalendar
                            value={endDateObj}
                            onChange={(newValue: Date | null) => {
                              setEndDateObj(newValue);
                              if (newValue) {
                                setEndDate(
                                  newValue.toISOString().split('T')[0],
                                );
                              }
                            }}
                            minDate={startDateObj || undefined}
                            maxDate={new Date()}
                            sx={{
                              '& .MuiPickersCalendarHeader-root': {
                                fontSize: '0.875rem',
                              },
                              '& .MuiDayCalendar-weekDayLabel': {
                                fontSize: '0.75rem',
                              },
                              '& .MuiPickersDay-root': { fontSize: '0.75rem' },
                              '& .MuiPickersDay-root.Mui-selected': {
                                backgroundColor: '#00387D',
                              },
                            }}
                          />
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 border-t border-[#E4EBF4] pt-2">
                        <button
                          type="button"
                          onClick={() => setIsCalendarOpen(false)}
                          className="h-8 px-3 text-[13px] font-semibold text-[#252525] rounded-md hover:bg-[#F7F9FC]"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsCalendarOpen(false)}
                          className="h-8 px-3 text-[13px] font-semibold text-white bg-[#00387D] hover:bg-[#002A5F] rounded-md"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </LocalizationProvider>

              <div className="inline-flex bg-[#F7F9FC] border-[1.5px] border-[#E5E7EB] rounded-lg p-[3px]">
                {intervalOptions.map(opt => (
                  <button
                    type="button"
                    key={opt.value}
                    onClick={() => setTimePeriod(opt.value)}
                    className={`px-3 py-1.5 rounded-md text-[13px] font-semibold transition-colors ${
                      timePeriod === opt.value
                        ? 'bg-white text-[#0C2340] shadow-[0_1px_3px_rgba(12,35,64,0.12)]'
                        : 'text-[#5B6B80] hover:text-[#0C2340]'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Filters: flows (left) + validation results as legend (right) */}
          <div className="flex flex-wrap items-center justify-between gap-4 mt-4 pt-4 border-t border-[#E4EBF4]">
            <div className="flex gap-2 flex-wrap max-h-[120px] overflow-y-auto min-w-0">
              <button
                type="button"
                onClick={selectAllFlows}
                className={chipClass(allFlowsSelected)}
              >
                All flows
              </button>
              {selectableFlows.map(flow => (
                <button
                  type="button"
                  key={flow.id}
                  onClick={() => toggleFlowSelection(flow.id)}
                  className={chipClass(
                    !allFlowsMode && selectedFlowIds.includes(flow.id),
                  )}
                  title={flow.id}
                >
                  {flow.name}
                </button>
              ))}
            </div>

            <div className="flex gap-1.5 flex-wrap">
              <span className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border-[1.5px] border-[#E5E7EB] text-[13px] font-semibold text-[#252525]">
                <span
                  className="w-2.5 h-2.5 rounded-[3px]"
                  style={{ backgroundColor: TOTAL_COMPLETED_TREND.color }}
                />
                Total completed
              </span>
              {VALIDATION_TREND_META.map(meta => {
                const on = selectedValidations.includes(meta.key);
                return (
                  <button
                    type="button"
                    key={meta.key}
                    onClick={() => toggleValidationSelection(meta.key)}
                    title={
                      on && selectedValidations.length === 1
                        ? 'At least one result must remain selected'
                        : undefined
                    }
                    className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border-[1.5px] text-[13px] font-semibold transition-colors ${
                      on
                        ? 'border-[#E5E7EB] bg-white text-[#252525] hover:border-[#00387D]'
                        : 'border-[#E5E7EB] bg-[#F7F9FC] text-[#5B6B80]'
                    }`}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-[3px]"
                      style={{ backgroundColor: on ? meta.color : '#D1D5DB' }}
                    />
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          <p className="text-xs text-[#5B6B80] mt-2">
            {allFlowsSelected
              ? 'All flows aggregated'
              : `${activeFlowIds.length} flow${activeFlowIds.length === 1 ? '' : 's'} selected`}
          </p>

          <div
            ref={chartContainerRef}
            className="w-full relative h-[340px] -mx-2"
          >
            {isLoadingTrends ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#00387D]" />
                  <p className="mt-2 text-sm text-[#5B6B80]">Loading trends…</p>
                </div>
              </div>
            ) : (
              <Chart
                key={chartKey}
                options={currentChartOptions}
                series={currentChartData}
                type="line"
                height="100%"
              />
            )}
          </div>
        </section>

        {/* Right column */}
        <div className="flex flex-col gap-5">
          <section className="bg-white border-[1.5px] border-[#E5E7EB] rounded-[14px] p-[18px]">
            <h3 className="text-base font-extrabold text-[#0C2340]">
              Quick links
            </h3>
            <div className="flex flex-col gap-2 mt-3.5">
              {quickLinks.map(link => {
                const Icon = link.icon;
                const external = link.link.startsWith('http');
                return (
                  <button
                    type="button"
                    key={link.label}
                    onClick={() => {
                      if (external) {
                        window.open(link.link, '_blank', 'noopener noreferrer');
                      } else {
                        navigate(link.link);
                      }
                    }}
                    className="group relative overflow-hidden flex items-center gap-3 p-3 text-left rounded-[14px] border-[1.5px] border-[#E5E7EB] bg-white transition-all duration-300 hover:-translate-y-0.5 hover:border-[#00387D] hover:shadow-[0_8px_24px_rgba(0,56,125,0.12)]"
                  >
                    <span
                      className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#00387D] to-[#6ECF89] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-[420ms]"
                      aria-hidden="true"
                    />
                    <Icon
                      className="w-[22px] h-[22px] text-[#5B6B80] group-hover:text-[#00387D] transition-colors flex-shrink-0"
                      strokeWidth={1.75}
                    />
                    <span className="min-w-0">
                      <span className="block text-[14.5px] font-semibold text-[#0C2340]">
                        {link.label}
                      </span>
                      <span className="block text-[12.5px] text-[#5B6B80]">
                        {link.description}
                      </span>
                    </span>
                    {external ? (
                      <ArrowUpRight className="ml-auto w-4 h-4 text-[#5B6B80] group-hover:text-[#00387D]" />
                    ) : (
                      <ChevronRight className="ml-auto w-4 h-4 text-[#5B6B80] group-hover:text-[#00387D] group-hover:translate-x-0.5 transition-all" />
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="bg-white border-[1.5px] border-[#E5E7EB] rounded-[14px] p-[18px] flex-1 flex flex-col">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-extrabold text-[#0C2340]">
                Live activity
              </h3>
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#00387D] bg-[#EAF0F9] px-2 py-[3px] rounded-full">
                Soon
              </span>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center text-center gap-2.5 px-3 py-7 mt-3.5 rounded-xl bg-[#F7F9FC]">
              <Activity
                className="w-[26px] h-[26px] text-[#5B6B80]"
                strokeWidth={1.75}
              />
              <p className="text-[15px] font-bold text-[#0C2340]">
                Real-time feed is on the way
              </p>
              <p className="text-[13px] text-[#5B6B80] max-w-[220px] leading-snug">
                You&apos;ll see session events, reviews and team changes here as
                they happen.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default SonioDashboard;
